import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';


interface TabItem {
  label: string;
  icon: string;
  id: string;
}

interface QuickAccessLink {
  label: string;
  icon: string;
  route: string;
  description: string;
  color: string;
}


@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {
  activeTab: string = 'profile';
  currentUser: any = null;
  isLoading: boolean = false;
  isHeaderCollapsed: boolean = false;

  // Forms
  profileForm!: FormGroup;
  passwordForm!: FormGroup;
  preferencesForm!: FormGroup;

  // Tabs
  tabs: TabItem[] = [
    { label: 'Profile', icon: 'pi-user', id: 'profile' },
    { label: 'Preferences', icon: 'pi-cog', id: 'preferences' },
    { label: 'Quick Access', icon: 'pi-bolt', id: 'quick-access' }
  ];

  // Quick Access Links
  quickAccessLinks: QuickAccessLink[] = [
    {
      label: 'Order Number Generator',
      icon: 'pi-hashtag',
      route: '/quotation-part/Order-number-genarate',
      description: 'Generate and manage order numbers for quotations',
      color: '#2563eb'
    },
    {
      label: 'Dashboard',
      icon: 'pi-chart-line',
      route: '/quotation-part/quotation-dashboard',
      description: 'View analytics and performance metrics',
      color: '#10b981'
    }
  ];

  // Password visibility
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  // Theme options
  themeOptions = [
    { label: 'Light Mode', value: 'light', icon: 'pi-sun' },
    { label: 'Dark Mode', value: 'dark', icon: 'pi-moon' },
    { label: 'Auto (System)', value: 'auto', icon: 'pi-desktop' }
  ];

  // Language options
  languageOptions = [
    { label: 'English', value: 'en', flag: '🇬🇧' },
    { label: 'Sinhala', value: 'si', flag: '🇱🇰' },
    { label: 'Tamil', value: 'ta', flag: '🇱🇰' }
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.initializeForms();
  }

  onHeaderCollapse(collapsed: boolean): void {
    this.isHeaderCollapsed = collapsed;
  }

  loadCurrentUser(): void {
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser) {
      this.profileForm?.patchValue({
        name: this.currentUser.name,
        email: this.currentUser.email,
        employeeNumber: this.currentUser.employeenumber,
        role: this.currentUser.role
      });
    }
  }

  initializeForms(): void {
    // Profile Form
    this.profileForm = this.fb.group({
      name: [this.currentUser?.name || '', [Validators.required, Validators.minLength(3)]],
      email: [this.currentUser?.email || '', [Validators.required, Validators.email]],
      employeeNumber: [{ value: this.currentUser?.employeenumber || '', disabled: true }],
      role: [{ value: this.currentUser?.role || '', disabled: true }]
    });

    // Password Form
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required, Validators.minLength(6)]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });

    // Preferences Form
    this.preferencesForm = this.fb.group({
      theme: ['light'],
      language: ['en'],
      emailNotifications: [true],
      systemAlerts: [true],
      quotationUpdates: [false],
      autoSave: [true],
      compactView: [false],
      showTutorials: [true]
    });

    this.loadPreferences();
  }

  loadPreferences(): void {
    const savedPrefs = localStorage.getItem('userPreferences');
    if (savedPrefs) {
      this.preferencesForm.patchValue(JSON.parse(savedPrefs));
    }
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { mismatch: true };
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
  }

  navigateToLink(route: string): void {
    this.router.navigate([route]);
  }

  onUpdateProfile(): void {
    if (this.profileForm.invalid) {
      this.markFormGroupTouched(this.profileForm);
      this.showError('Please fill all required fields correctly');
      return;
    }

    this.isLoading = true;
    const profileData = this.profileForm.getRawValue();

    // Simulate API call
    setTimeout(() => {
      const updatedUser = { ...this.currentUser, ...profileData };
      this.authService.setCurrentUser(updatedUser);
      this.currentUser = updatedUser;
      
      this.isLoading = false;
      this.showSuccess('Profile updated successfully');
    }, 1000);
  }

  onChangePassword(): void {
    if (this.passwordForm.invalid) {
      this.markFormGroupTouched(this.passwordForm);
      if (this.passwordForm.hasError('mismatch')) {
        this.showError('New password and confirm password do not match');
      } else {
        this.showError('Please fill all password fields correctly');
      }
      return;
    }

    this.isLoading = true;

    setTimeout(() => {
      this.isLoading = false;
      this.passwordForm.reset();
      this.showSuccess('Password changed successfully');
    }, 1000);
  }

  onSavePreferences(): void {
    const preferences = this.preferencesForm.value;
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    
    this.applyTheme(preferences.theme);
    
    this.showSuccess('Preferences saved successfully');
  }

  applyTheme(theme: string): void {
    document.body.setAttribute('data-theme', theme);
  }

  togglePasswordVisibility(field: string): void {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message,
      life: 3000
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 3000
    });
  }

  // Getters
  get name() { return this.profileForm.get('name'); }
  get email() { return this.profileForm.get('email'); }
  get currentPassword() { return this.passwordForm.get('currentPassword'); }
  get newPassword() { return this.passwordForm.get('newPassword'); }
  get confirmPassword() { return this.passwordForm.get('confirmPassword'); }
}